import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  User,
  UserFilter,
  UserResponse,
  CreateUserPayload,
  UpdateUserStatusPayload,
  ChangePasswordPayload,
} from "../types/staff.types";

type StaffDetailResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: { user: User } | User;
};

type StaffMutationResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: unknown;
};

// all fields optional on update; the backend only changes what is sent
type StaffUpdatePayload = Partial<Omit<CreateUserPayload, "password">> & {
  password?: string;
};

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.staff;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const staffService = {
  async list(filter: UserFilter): Promise<UserResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<UserResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load staff due to an unexpected error.",
      ) as unknown as UserResponse;
    }
  },

  // single user is fetched with both userid and role_code as query params
  async details(
    userId: number | string,
    roleCode: number | string,
  ): Promise<StaffDetailResponse> {
    try {
      const params = new URLSearchParams({
        userid: String(userId),
        role_code: String(roleCode),
      });
      return await apiClient<StaffDetailResponse>(endpoints.getSingleStaff(userId, roleCode), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
      // return await apiClient<StaffDetailResponse>(endpoints.list(params.toString()), {
      //   method: "GET",
      //   isAuthenticated: true,
      //   cache: "no-store",
      // });
    } catch (error) {
      return failure(
        error,
        "Failed to load staff details due to an unexpected error.",
      ) as unknown as StaffDetailResponse;
    }
  },

  async detailsByCode(staffCode: string): Promise<StaffDetailResponse> {
    try {
      return await apiClient<StaffDetailResponse>(
        endpoints.staffByCode(encodeURIComponent(staffCode)),
        {
          method: "GET",
          isAuthenticated: true,
          cache: "no-store",
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load staff details due to an unexpected error.",
      ) as unknown as StaffDetailResponse;
    }
  },

  async create(payload: CreateUserPayload): Promise<StaffMutationResponse> {
    try {
      return await apiClient<StaffMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create staff due to an unexpected error.",
      ) as StaffMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: StaffUpdatePayload,
  ): Promise<StaffMutationResponse> {
    try {
      return await apiClient<StaffMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update staff due to an unexpected error.",
      ) as StaffMutationResponse;
    }
  },

  async updateStatus(
    payload: UpdateUserStatusPayload,
  ): Promise<StaffMutationResponse> {
    try {
      return await apiClient<StaffMutationResponse>(endpoints.updateStatus, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update staff status due to an unexpected error.",
      ) as StaffMutationResponse;
    }
  },

  async changePassword(
    payload: ChangePasswordPayload,
  ): Promise<StaffMutationResponse> {
    try {
      return await apiClient<StaffMutationResponse>(endpoints.changePassword, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to change password due to an unexpected error.",
      ) as StaffMutationResponse;
    }
  },

  async delete(id: number | string): Promise<StaffMutationResponse> {
    try {
      return await apiClient<StaffMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete staff due to an unexpected error.",
      ) as StaffMutationResponse;
    }
  },
};
